import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import toast from "react-hot-toast";
import ReactAudioPlayer from "react-audio-player";
import {
    Send,
    Paperclip,
    Clock,
    X,
    Download,
    Mic,
    Video,
    FileUp,
    Camera,
    Brush,
    Circle,
    Reply
} from 'lucide-react';

import AudioRecorder from "./audioRecorder";
import VideoRecorder from "./videoRecorder";
import ScreenRecorder from "./screenRecorder";
import ScreenshotCapture from "./screenshotCapture";

import { BACKEND_API_URL } from "../utils/api";
import { filterMentionSuggestions } from "@/utils/mentionUtils";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import * as nodeEmoji from 'node-emoji';

const REACT_APP_BACKEND_API_URL = BACKEND_API_URL;

// Draft message storage keyed by channelId (session-scoped, survives remounts)
const channelDrafts = {};

// Detect if a line looks like ASCII art or a diagram that needs space preservation
// Returns true if the line contains patterns typical of ASCII art
const isAsciiArtLine = (line) => {
    // Check for vertical line characters (common in diagrams) - any line with | is likely ASCII art
    if (/\|/.test(line)) return true;

    // Check for horizontal lines made of repeated characters (=== or --- with 4+ chars)
    if (/[=]{4,}|[-]{4,}/.test(line)) return true;

    // Check for box-drawing patterns like [Text] with significant spacing between brackets
    if (/\[.+\].*  .*\[.+\]/.test(line)) return true;

    // Check for tree/hierarchy drawing characters
    if (/[├└┌┐│─┬┴┼]/.test(line)) return true;

    // Check for lines that are mostly whitespace with bracketed text (diagram headers/nodes)
    if (/^\s*\[.+\]\s*$/.test(line) && /  /.test(line)) return true;

    return false;
};

// Comprehensive emoji conversion function that handles all Unicode emojis
// First tries nodeEmoji.unemojify, then encodes any remaining emojis as Unicode escape sequences
// This preserves all emojis while preventing database encoding errors
// Preserves newlines and other whitespace
const convertEmojisToShortcodes = (text) => {
    if (!text || typeof text !== 'string') return text || '';

    try {
        // First, try nodeEmoji.unemojify for known emojis (converts to :shortcode: format)
        let converted = nodeEmoji.unemojify(text);

        // Comprehensive emoji regex covering all Unicode emoji ranges
        // Includes: Miscellaneous Symbols (2600-26FF), Geometric Shapes Extended (1F780-1F7FF), etc.
        const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FAFF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F780}-\u{1F7FF}]|[\u{1FA70}-\u{1FAFF}]/gu;

        // Instead of removing unrecognized emojis, encode them as [U+XXXX] format
        // This preserves the emoji information while making it database-safe
        converted = converted.replace(emojiRegex, (match) => {
            // Get the Unicode code point(s) for the emoji
            // Handle multi-codepoint emojis (like flags or skin tone modifiers)
            let codePoints = [];
            for (let i = 0; i < match.length; i++) {
                const codePoint = match.codePointAt(i);
                if (codePoint >= 0x1F300) { // Only encode actual emoji ranges
                    codePoints.push(codePoint.toString(16).toUpperCase().padStart(4, '0'));
                    // Skip the next character if it's a surrogate pair
                    if (codePoint > 0xFFFF) i++;
                } else {
                    // Non-emoji character, keep as-is
                    return match[i];
                }
            }
            // Encode as [U+XXXX] format for single emoji or [U+XXXX][U+YYYY] for multi-codepoint
            return codePoints.map(cp => `[U+${cp}]`).join('');
        });

        // Clean up multiple spaces but preserve ASCII art, list indentation, and code blocks
        const lines = converted.split('\n');
        const processedLines = lines.map(line => {
            // ASCII art detection - preserve all spaces for diagram lines
            if (isAsciiArtLine(line)) {
                return line; // Preserve exactly as-is
            }

            // Check if line starts with list marker (preserve indentation)
            const listMatch = line.match(/^(\s*)([-*+]|\d+\.)\s/);
            if (listMatch) {
                // This is a list item - preserve leading indentation, only collapse spaces after the marker
                const [, indent, marker] = listMatch;
                const content = line.substring(listMatch[0].length);
                // Collapse multiple spaces in content only
                const cleanedContent = content.replace(/[ \t]{2,}/g, ' ');
                return indent + marker + ' ' + cleanedContent;
            }
            // Check if line is indented code or preserves structure
            if (/^\s{2,}/.test(line)) {
                // Heavily indented line - might be code or nested content, preserve indentation
                const match = line.match(/^(\s+)(.*)$/);
                if (match) {
                    const [, indent, content] = match;
                    // Only collapse spaces in content, preserve leading indent
                    const cleanedContent = content.replace(/[ \t]{2,}/g, ' ');
                    return indent + cleanedContent;
                }
            }
            // Regular line - collapse multiple spaces/tabs
            return line.replace(/[ \t]+/g, ' ');
        });

        return processedLines.join('\n');
    } catch (error) {
        console.warn('Error converting emojis:', error);
        // If conversion fails, try to encode emojis directly as Unicode escapes
        try {
            const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FAFF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F780}-\u{1F7FF}]|[\u{1FA70}-\u{1FAFF}]/gu;
            const encoded = text.replace(emojiRegex, (match) => {
                let codePoints = [];
                for (let i = 0; i < match.length; i++) {
                    const codePoint = match.codePointAt(i);
                    if (codePoint >= 0x1F300) {
                        codePoints.push(codePoint.toString(16).toUpperCase().padStart(4, '0'));
                        if (codePoint > 0xFFFF) i++;
                    } else {
                        return match[i];
                    }
                }
                return codePoints.map(cp => `[U+${cp}]`).join('');
            });
            // Preserve ASCII art and list indentation in fallback encoding too
            const lines = encoded.split('\n');
            const processedLines = lines.map(line => {
                // ASCII art detection - preserve all spaces for diagram lines
                if (isAsciiArtLine(line)) {
                    return line;
                }
                const listMatch = line.match(/^(\s*)([-*+]|\d+\.)\s/);
                if (listMatch) {
                    const [, indent, marker] = listMatch;
                    const content = line.substring(listMatch[0].length);
                    const cleanedContent = content.replace(/[ \t]{2,}/g, ' ');
                    return indent + marker + ' ' + cleanedContent;
                }
                if (/^\s{2,}/.test(line)) {
                    const match = line.match(/^(\s+)(.*)$/);
                    if (match) {
                        const [, indent, content] = match;
                        const cleanedContent = content.replace(/[ \t]{2,}/g, ' ');
                        return indent + cleanedContent;
                    }
                }
                return line.replace(/[ \t]+/g, ' ');
            });
            return processedLines.join('\n');
        } catch (e) {
            console.error('Failed to encode emojis:', e);
            // Last resort: remove emojis to prevent database errors
            const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FAFF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F780}-\u{1F7FF}]|[\u{1FA70}-\u{1FAFF}]/gu;
            return text.replace(emojiRegex, '').replace(/[ \t]+/g, '') || '';
        }
    }
};

const CreateMessage = ({ addMessage, setMessages, channelId, threadId, parentMessageId, repliedToMessage, onCancelReply }) => {
    const [mediaFile, setMediaFile] = useState(null);
    const [mediaType, setMediaType] = useState('');
    const [message, setMessage] = useState('');
    const [transcribedMessage, setTranscribedMessage] = useState('');
    const [partialTranscribedMessage, setPartialTranscribedMessage] = useState('');
    const [isMediaSelected, setIsMediaSelected] = useState(false);
    const [submitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef(null);
    const generalFileInputRef = useRef(null);
    const videoFileInputRef = useRef(null);
    const videoRef = useRef(null);
    const clearRecordingRef = useRef(null);
    const clearVideoRecordingRef = useRef(null);
    const clearScreenRecordingRef = useRef(null);
    const audioRecorderButtonRef = useRef(null);
    const inputRef = useRef(null);
    const [showRecordingDialog, setShowRecordingDialog] = useState(false);
    const [showAnnotationDialog, setShowAnnotationDialog] = useState(false);
    const [annotationImage, setAnnotationImage] = useState(null);
    const annotationCanvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const imageLoadedRef = useRef(false);
    const [brushSize, setBrushSize] = useState('10');
    const [screenshotCanvas, setScreenshotCanvas] = useState(null);
    const [screenshotQuality, setScreenshotQuality] = useState('0.75');
    const [mostRecentlyUsed, setMostRecentlyUsed] = useState(() => {
        return localStorage.getItem('mostRecentlyUsedMediaOption') || 'audio';
    });
    const [mentionQuery, setMentionQuery] = useState('');
    const [mentionSuggestions, setMentionSuggestions] = useState([]);
    const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
    const [mentionStartIndex, setMentionStartIndex] = useState(-1);
    const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
    const mentionSuggestionsRef = useRef(null);
    const mobileScreen = window?.innerWidth <= 600;

    // Ref to always track the latest message value for draft saving
    const messageRef = useRef(message);
    messageRef.current = message;

    // Save/restore draft messages when switching channels or threads
    useEffect(() => {
        const draftKey = threadId != null ? `thread-${channelId}-${threadId}` : `channel-${channelId}`;

        // Restore draft for the newly selected channel/thread
        const savedDraft = channelDrafts[draftKey] || '';
        setMessage(savedDraft);

        return () => {
            // Save current message as draft when leaving this channel/thread
            const currentMessage = messageRef.current;
            if (currentMessage && currentMessage.trim()) {
                channelDrafts[draftKey] = currentMessage;
            } else {
                delete channelDrafts[draftKey];
            }
        };
    }, [channelId, threadId]);

    useEffect(() => {
        if (transcribedMessage.length || partialTranscribedMessage.length) {
            setMessage(transcribedMessage + partialTranscribedMessage);
        }
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [transcribedMessage, partialTranscribedMessage]);

    // Focus textarea when reply is selected
    useEffect(() => {
        if (repliedToMessage && inputRef.current) {
            // Use setTimeout to ensure the DOM is updated
            setTimeout(() => {
                inputRef.current?.focus();
            }, 0);
        }
    }, [repliedToMessage]);

    // Fetch users for mention suggestions
    useEffect(() => {
        const fetchUsers = async () => {
            if (mentionQuery.length > 0) {
                try {
                    const accessToken = Cookies.get('access_token');
                    const response = await axios.get(`${REACT_APP_BACKEND_API_URL}/api/users_and_tags/`, {
                        headers: {
                            'Authorization': `Token ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                    });

                    const filtered = filterMentionSuggestions(response.data.users, mentionQuery);

                    setMentionSuggestions(filtered);
                    setShowMentionSuggestions(filtered.length > 0);
                    setSelectedMentionIndex(0);
                } catch (error) {
                    console.error('Error fetching users:', error);
                }
            } else {
                setMentionSuggestions([]);
                setShowMentionSuggestions(false);
            }
        };

        fetchUsers();
    }, [mentionQuery]);

    const handleTranscriptionStart = () => {
        setTranscribedMessage(message);
    };

    const handleTranscriptionEnd = () => {
        setPartialTranscribedMessage("");
        setTranscribedMessage(message);
    };

    const handleTranscriptionChange = (msg, is_partial) => {
        if (is_partial) {
            setPartialTranscribedMessage(msg);
        } else {
            setPartialTranscribedMessage("");
            setTranscribedMessage(prevMsg => prevMsg + " " + msg);
        }
    };

    // Helper functions
    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
        return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    };

    const getHumanReadableTimestamp = () => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}-${String(now.getMilliseconds()).padStart(3, '0')}`;
    };

    const addTimestampToFilename = (file) => {
        const humanTimestamp = getHumanReadableTimestamp();
        const originalName = file.name;
        const lastDotIndex = originalName.lastIndexOf('.');

        let newFilename;
        if (lastDotIndex === -1) {
            newFilename = `${originalName}_${humanTimestamp}`;
        } else {
            const nameWithoutExt = originalName.substring(0, lastDotIndex);
            const extension = originalName.substring(lastDotIndex);
            newFilename = `${nameWithoutExt}_${humanTimestamp}${extension}`;
        }

        return new File([file], newFilename, {
            type: file.type,
            lastModified: Date.now()
        });
    };

    // Helper function to detect file type by extension as fallback
    const detectFileType = (file) => {
        // First try MIME type
        if (file.type) {
            if (file.type.startsWith('video')) return 'video';
            if (file.type.startsWith('image')) return 'image';
            if (file.type.startsWith('audio')) return 'audio';
        }

        // Fallback to file extension
        const fileName = file.name.toLowerCase();
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.ico'];
        const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv', '.flv', '.wmv'];
        const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.wma'];

        const extension = fileName.substring(fileName.lastIndexOf('.'));

        if (imageExtensions.includes(extension)) return 'image';
        if (videoExtensions.includes(extension)) return 'video';
        if (audioExtensions.includes(extension)) return 'audio';

        return 'other';
    };

    // 500MB file size limit
    const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB in bytes

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check file size limit (500MB)
            if (file.size > MAX_FILE_SIZE) {
                toast.error(`File size exceeds 500MB limit. Your file is ${formatFileSize(file.size)}.`);
                return;
            }

            const timestampedFile = addTimestampToFilename(file);
            const detectedType = detectFileType(file);

            if (detectedType === 'video') {
                setMediaFile(timestampedFile);
                setIsMediaSelected(true);
                setMediaType('video');
                setShowRecordingDialog(true);
            } else if (detectedType === 'image') {
                // Show annotation dialog for all image uploads (mobile and desktop)
                const reader = new FileReader();
                reader.onload = (event) => {
                    setAnnotationImage(event.target.result);
                    setShowAnnotationDialog(true);
                };
                reader.readAsDataURL(file);
                setMediaFile(timestampedFile);
            } else if (detectedType === 'audio') {
                setMediaFile(timestampedFile);
                setIsMediaSelected(true);
                setMediaType('audio');
                setShowRecordingDialog(true);
            } else {
                // Support all other file types (PDFs, docs, etc.)
                setMediaFile(timestampedFile);
                setIsMediaSelected(true);
                setMediaType('other');
                setShowRecordingDialog(true);
            }

            if (videoRef.current) {
                videoRef.current.load();
            }
        }
    };

    const handleUploadButtonClick = () => {
        generalFileInputRef.current.click();
    };

    const handleRecordingComplete = (blob) => {
        const humanTimestamp = getHumanReadableTimestamp();
        const audioFile = new File([blob], `audio_recording_${humanTimestamp}.wav`, {
            type: 'audio/wav',
            lastModified: Date.now()
        });
        setMediaFile(audioFile);
        setIsMediaSelected(true);
        setMediaType('audio');
        setShowRecordingDialog(true);
    };

    const handleVideoRecordingComplete = (blob) => {
        const humanTimestamp = getHumanReadableTimestamp();
        const videoFile = new File([blob], `video_recording_${humanTimestamp}.webm`, {
            type: 'video/webm',
            lastModified: Date.now()
        });
        setMediaFile(videoFile);
        setIsMediaSelected(true);
        setMediaType('video');
        setShowRecordingDialog(true);
    };

    const handleScreenRecordingComplete = (blob) => {
        const humanTimestamp = getHumanReadableTimestamp();
        const screenFile = new File([blob], `screen_recording_${humanTimestamp}.webm`, {
            type: 'video/webm',
            lastModified: Date.now()
        });
        setMediaFile(screenFile);
        setIsMediaSelected(true);
        setMediaType('video');
        setShowRecordingDialog(true);
    };

    const handleScreenshotComplete = (canvas) => {
        // Convert canvas to data URL and show annotation dialog
        const dataUrl = canvas.toDataURL('image/png');
        setAnnotationImage(dataUrl);
        setScreenshotCanvas(canvas);
        setShowAnnotationDialog(true);
    };

    const generateScreenshotBlob = (canvas, quality) => {
        canvas.toBlob((blob) => {
            const humanTimestamp = getHumanReadableTimestamp();
            const screenshotFile = new File([blob], `screenshot_${humanTimestamp}.jpg`, {
                type: 'image/jpeg',
                lastModified: Date.now()
            });
            setMediaFile(screenshotFile);
            setIsMediaSelected(true);
            setMediaType('image');
            setShowRecordingDialog(true);
        }, 'image/jpeg', quality);
    };

    const handleScreenshotQualityChange = (value) => {
        if (!value) return;
        const quality = parseFloat(value);
        setScreenshotQuality(value);
        if (screenshotCanvas) {
            generateScreenshotBlob(screenshotCanvas, quality);
        }
    };

    const handleSendRecording = () => {
        setShowRecordingDialog(false);
        setScreenshotCanvas(null);
        setScreenshotQuality('0.75');
        handleSubmit();
    };

    const handleCancelRecording = () => {
        setShowRecordingDialog(false);
        setScreenshotCanvas(null);
        setScreenshotQuality('0.75');
        clearMedia();
    };

    const handleDownloadRecording = () => {
        if (mediaFile) {
            const url = URL.createObjectURL(mediaFile);
            const link = document.createElement('a');
            link.href = url;
            link.download = mediaFile.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success('Recording downloaded!');
        }
    };

    const handleRecordingTypeSelect = (type) => {
        setMostRecentlyUsed(type);
        localStorage.setItem('mostRecentlyUsedMediaOption', type);

        if (type === 'photo') {
            fileInputRef.current.click();
            return;
        }
        if (type === 'video-native') {
            videoFileInputRef.current.click();
            return;
        }
        if (type === 'file') {
            generalFileInputRef.current.click();
            return;
        }

        setTimeout(() => {
            let button = null;
            if (type === 'audio' && audioRecorderButtonRef.current) {
                button = audioRecorderButtonRef.current.querySelector('button');
            }
            if (button) {
                button.click();
            }
        }, 100);
    };

    const clearMedia = () => {
        setMediaFile(null);
        setMediaType('');
        setIsMediaSelected(false);
        setTranscribedMessage('');
        setMessage('');
        if (clearRecordingRef.current) clearRecordingRef.current();
        if (clearVideoRecordingRef.current) clearVideoRecordingRef.current();
        if (clearScreenRecordingRef.current) clearScreenRecordingRef.current();
    };

    const handleSubmit = () => {
        if (message || mediaFile) {
            setIsSubmitting(true);
            const formData = new FormData();

            if (mediaFile) {
                formData.append('media_file', mediaFile);
                formData.append('media_type', mediaType);
                formData.append('media_size', mediaFile.size);
            }

            // Convert emojis to shortcodes (e.g., 🔥 → :fire:) for database storage
            // Use comprehensive conversion that handles all Unicode emojis
            const messageToSend = convertEmojisToShortcodes(message || '');
            formData.append('message', messageToSend);

            if (threadId !== null) {
                formData.append('thread_id', threadId);
            }
            if (parentMessageId !== null) {
                formData.append('parent_message_id', parentMessageId);
            }
            if (repliedToMessage && repliedToMessage.id) {
                formData.append('replied_to_message_id', repliedToMessage.id);
            }

            const accessToken = Cookies.get('access_token');
            axios.post(`${REACT_APP_BACKEND_API_URL}/api/channels/${channelId}/message/create/`, formData, {
                headers: {
                    Authorization: `Token ${accessToken}`,
                    'Content-Type': 'multipart/form-data',
                },
            })
                .then(response => {
                    // Use addMessage callback if available (prevents race condition with polling)
                    // Fall back to setMessages for backwards compatibility (e.g., ThreadView)
                    if (addMessage) {
                        addMessage(response.data.message);
                    } else if (setMessages) {
                        setMessages(prevMessages => [...prevMessages, response.data.message]);
                    }
                    toast.success('Message saved successfully!');
                    clearMedia();
                    setIsSubmitting(false);
                    // Clear draft for this channel/thread after successful send
                    const draftKey = threadId != null ? `thread-${channelId}-${threadId}` : `channel-${channelId}`;
                    delete channelDrafts[draftKey];
                    // Clear reply selection after sending
                    if (onCancelReply) {
                        onCancelReply();
                    }
                })
                .catch(error => {
                    console.error('Message send error:', error);
                    let err_message = 'Failed to send message.';
                    if (error?.response?.data?.message) {
                        err_message = error.response.data.message;
                    } else if (error?.response?.data?.error) {
                        err_message = error.response.data.error;
                    } else if (error?.message) {
                        err_message = error.message;
                    }
                    toast.error(err_message);
                    setIsSubmitting(false);
                });
        } else {
            toast.error('Incomplete form data.');
        }
    };

    const handleMentionSelect = (user) => {
        const textBefore = message.substring(0, mentionStartIndex);
        const textAfter = message.substring(mentionStartIndex).replace(/@[^\s@]*/, `@${user.username} `);
        setMessage(textBefore + textAfter);
        setShowMentionSuggestions(false);
        setMentionQuery('');
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 0);
    };

    // Convert HTML to markdown-like structured text
    const htmlToStructuredText = (html) => {
        if (!html) return '';

        // Strip all style and class attributes to prevent theme issues
        const cleanHtml = html
            .replace(/style\s*=\s*["'][^"']*["']/gi, '')
            .replace(/class\s*=\s*["'][^"']*["']/gi, '')
            .replace(/data-[^=]*=["'][^"']*["']/gi, '');

        // Create a temporary div to parse HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = cleanHtml;

        // Helper function to calculate list depth by counting parent lists
        const getListDepth = (node) => {
            let depth = 0;
            let parent = node.parentElement;
            while (parent && parent !== tempDiv) {
                const tagName = parent.tagName?.toLowerCase();
                if (tagName === 'ul' || tagName === 'ol') {
                    depth++;
                }
                parent = parent.parentElement;
            }
            return depth;
        };

        // Helper function to process nodes recursively
        const processNode = (node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                const text = node.textContent || '';
                return text;
            }

            if (node.nodeType !== Node.ELEMENT_NODE) {
                return '';
            }

            const tagName = node.tagName?.toLowerCase();
            const children = Array.from(node.childNodes);

            // Process children
            let content = '';

            for (const child of children) {
                if (child.nodeType === Node.ELEMENT_NODE) {
                    // Process child node (depth is calculated via getListDepth when needed)
                    content += processNode(child);
                } else if (child.nodeType === Node.TEXT_NODE) {
                    const text = child.textContent || '';
                    // Preserve text in list items, trim elsewhere
                    if (tagName === 'li') {
                        content += text;
                    } else {
                        content += text.trim();
                    }
                }
            }

            switch (tagName) {
                case 'p':
                    return content.trim() ? `${content.trim()}\n\n` : '';

                case 'div':
                    return content.trim() ? `${content.trim()}\n\n` : '';

                case 'br':
                    return '\n';

                case 'ul':
                case 'ol':
                    // Lists return their content (already processed)
                    return content;

                case 'li':
                    const depth = getListDepth(node);
                    // Markdown requires 2 spaces per nesting level
                    // Level 1 (depth=1): 0 spaces, Level 2 (depth=2): 2 spaces, Level 3 (depth=3): 4 spaces, etc.
                    const indent = '  '.repeat(Math.max(0, depth - 1));
                    const parentTag = node.parentElement?.tagName?.toLowerCase();
                    const marker = parentTag === 'ol' ? '1.' : '-';

                    // Split content into text and nested lists
                    let textContent = '';
                    let nestedListContent = '';

                    // Check if this li contains nested lists
                    const hasNestedList = children.some(child => {
                        const childTag = child.tagName?.toLowerCase();
                        return childTag === 'ul' || childTag === 'ol';
                    });

                    if (hasNestedList) {
                        // Separate text from nested lists
                        for (const child of children) {
                            if (child.nodeType === Node.TEXT_NODE) {
                                const text = child.textContent || '';
                                textContent += text.trim() ? text.trim() + ' ' : '';
                            } else if (child.nodeType === Node.ELEMENT_NODE) {
                                const childTag = child.tagName?.toLowerCase();
                                if (childTag === 'ul' || childTag === 'ol') {
                                    // Nested list - process it (it will calculate its own depth)
                                    // The nested list items will have depth+1, so they'll be indented by 2 more spaces
                                    nestedListContent += processNode(child);
                                } else if (childTag === 'p' || childTag === 'div') {
                                    const processed = processNode(child).trim();
                                    textContent += processed ? processed + ' ' : '';
                                } else if (childTag === 'strong' || childTag === 'b' || childTag === 'em' || childTag === 'i') {
                                    textContent += processNode(child);
                                } else {
                                    textContent += processNode(child);
                                }
                            }
                        }

                        textContent = textContent.trim();
                        // Format: marker text, then nested list on next line
                        // Nested list items will already have proper 2-space indentation from their depth calculation
                        if (textContent) {
                            return `${indent}${marker} ${textContent}\n${nestedListContent}`;
                        } else {
                            return `${indent}${marker}\n${nestedListContent}`;
                        }
                    } else {
                        // Simple list item with no nested lists
                        const cleanContent = content.trim();
                        return `${indent}${marker} ${cleanContent}\n`;
                    }

                case 'strong':
                case 'b':
                    return `**${content}**`;

                case 'em':
                case 'i':
                    return `*${content}*`;

                case 'code':
                    // Don't add backticks if inside pre
                    if (node.parentElement?.tagName?.toLowerCase() === 'pre') {
                        return content;
                    }
                    return `\`${content}\``;

                case 'pre':
                    return `\n\`\`\`\n${content}\n\`\`\`\n`;

                case 'h1':
                    return `# ${content.trim()}\n\n`;
                case 'h2':
                    return `## ${content.trim()}\n\n`;
                case 'h3':
                    return `### ${content.trim()}\n\n`;
                case 'h4':
                    return `#### ${content.trim()}\n\n`;
                case 'h5':
                    return `##### ${content.trim()}\n\n`;
                case 'h6':
                    return `###### ${content.trim()}\n\n`;

                case 'blockquote':
                    return content.split('\n').map(line => line.trim() ? `> ${line.trim()}` : '').join('\n') + '\n\n';

                case 'a':
                    const href = node.getAttribute('href') || '';
                    const linkText = content || href;
                    return href && href !== linkText ? `[${linkText}](${href})` : linkText;

                case 'table':
                    return `\n${content}\n`;

                case 'thead':
                case 'tbody':
                    return content;

                case 'tr':
                    return `${content}|\n`;

                case 'th':
                case 'td':
                    return `${content.trim()} | `;

                case 'hr':
                    return '\n---\n\n';

                default:
                    return content;
            }
        };

        let result = processNode(tempDiv);

        // Clean up excessive newlines (max 2 consecutive, but preserve list structure)
        // Don't collapse newlines that are part of list structure
        result = result.replace(/\n{4,}/g, '\n\n\n');

        // Clean up trailing whitespace but preserve structure
        result = result.trim();

        return result;
    };

    // Handle paste events to capture rich content
    const handlePaste = (e) => {
        e.preventDefault();

        const clipboardData = e.clipboardData || window.clipboardData;

        // Check for image data in clipboard first (for screenshots/copied images)
        if (clipboardData.items && clipboardData.items.length > 0) {
            const items = Array.from(clipboardData.items);
            const imageItem = items.find(item => item.type.startsWith('image/'));

            if (imageItem) {
                let file = imageItem.getAsFile();
                if (file) {
                    // Ensure file has a name (clipboard images might not have one)
                    if (!file.name) {
                        const extension = imageItem.type.split('/')[1] || 'png';
                        file = new File([file], `screenshot.${extension}`, { type: imageItem.type });
                    }
                    // Process image like file upload - show annotation dialog
                    const timestampedFile = addTimestampToFilename(file);
                    const detectedType = detectFileType(timestampedFile);

                    if (detectedType === 'image') {
                        // Show annotation dialog for images
                        const reader = new FileReader();
                        reader.onload = (event) => {
                            setAnnotationImage(event.target.result);
                            setShowAnnotationDialog(true);
                        };
                        reader.readAsDataURL(timestampedFile);
                        setMediaFile(timestampedFile);
                    }
                    return; // Don't process text if image was found
                }
            }
        }

        // Fallback: Check clipboardData.files as alternative (for older browsers)
        if (clipboardData.files && clipboardData.files.length > 0) {
            const files = Array.from(clipboardData.files);
            const imageFile = files.find(file => file.type.startsWith('image/'));

            if (imageFile) {
                // Ensure file has a name (clipboard images might not have one)
                let fileToProcess = imageFile;
                if (!imageFile.name) {
                    const extension = imageFile.type.split('/')[1] || 'png';
                    fileToProcess = new File([imageFile], `screenshot.${extension}`, { type: imageFile.type });
                }
                // Process image like file upload
                const timestampedFile = addTimestampToFilename(fileToProcess);
                const detectedType = detectFileType(timestampedFile);

                if (detectedType === 'image') {
                    // Show annotation dialog for images
                    const reader = new FileReader();
                    reader.onload = (event) => {
                        setAnnotationImage(event.target.result);
                        setShowAnnotationDialog(true);
                    };
                    reader.readAsDataURL(timestampedFile);
                    setMediaFile(timestampedFile);
                }
                return; // Don't process text if image was found
            }
        }

        // No image found - process text/HTML as before
        const html = clipboardData.getData('text/html');
        const plainText = clipboardData.getData('text/plain');

        // Get current cursor position
        const textarea = e.target;
        const start = textarea.selectionStart || 0;
        const end = textarea.selectionEnd || 0;
        const currentValue = message;

        let textToInsert = '';

        // If HTML is available, convert it to structured markdown
        if (html && html.trim()) {
            textToInsert = htmlToStructuredText(html);
        } else if (plainText) {
            // Fallback to plain text if no HTML
            textToInsert = plainText;
        }

        // Insert the text at cursor position
        const newValue =
            currentValue.substring(0, start) +
            textToInsert +
            currentValue.substring(end);

        setMessage(newValue);

        // Restore cursor position after the inserted text
        setTimeout(() => {
            if (inputRef.current) {
                const newCursorPos = start + textToInsert.length;
                inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
                inputRef.current.focus();
            }
        }, 0);
    };

    if (!channelId) {
        return null;
    }

    const canSubmit = message !== '' || mediaFile !== null;

    // Media options
    const allMediaOptions = [
        { type: 'photo', icon: Camera, label: 'Photo' },
        { type: 'video-native', icon: Video, label: 'Video' },
        { type: 'file', icon: FileUp, label: 'Attach File' },
        { type: 'audio', icon: Mic, label: 'Audio Recording' }
    ];

    const outsideOption = allMediaOptions.find(opt => opt.type === mostRecentlyUsed) || allMediaOptions[3];
    const menuOptions = allMediaOptions.filter(opt => opt.type !== mostRecentlyUsed);

    return (
        <TooltipProvider>
            {/* Hidden file inputs */}
            <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileChange}
                ref={fileInputRef}
                className="hidden"
            />
            <input
                type="file"
                accept="video/*"
                capture="environment"
                onChange={handleFileChange}
                ref={videoFileInputRef}
                className="hidden"
            />
            <input
                type="file"
                onChange={handleFileChange}
                ref={generalFileInputRef}
                className="hidden"
            />

            {/* Main message input container - ChatGPT style */}
            <div className={cn(
                "relative",
                mobileScreen
                    ? "fixed bottom-0 left-0 right-0 px-3 py-2 bg-background z-50"
                    : "w-full p-1"
            )}>
                {/* Mention suggestions - positioned above the composer */}
                {showMentionSuggestions && mentionSuggestions.length > 0 && (
                    <div
                        ref={mentionSuggestionsRef}
                        className="absolute bottom-full left-3 right-3 bg-popover border shadow-lg max-h-[200px] overflow-y-auto z-50"
                    >
                        {mentionSuggestions.map((user, index) => (
                            <div
                                key={user.id}
                                className={cn(
                                    "flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors first:rounded-t-xl last:rounded-b-xl",
                                    index === selectedMentionIndex ? "bg-accent" : "hover:bg-muted/50"
                                )}
                                onClick={() => handleMentionSelect(user)}
                            >
                                <Avatar className="h-8 w-8 ring-2 ring-background">
                                    <AvatarImage src={user.picture} />
                                    <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
                                        {user.first_name?.[0] || user.username[0]}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="flex flex-col min-w-0">
                                    <span className="text-sm font-medium truncate">{user.username}</span>
                                    <span className="text-xs text-muted-foreground truncate">
                                        {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Composer box */}
                <div className={cn(
                    "flex flex-col bg-muted/50 rounded-3xl px-3 py-2",
                    "border border-border/50"
                )}>
                    {/* Quoted Reply Preview (WhatsApp-style) */}
                    {repliedToMessage && (
                        <div className="mb-2 px-2 py-1.5 rounded border-l-3 bg-primary/20 border-primary/50 dark:bg-primary/10 flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 mb-0.5">
                                    <Reply className="h-3 w-3 text-primary shrink-0" />
                                    <span className="text-[10px] font-semibold text-foreground/80">
                                        Replying to {repliedToMessage.author?.first_name} {repliedToMessage.author?.last_name}
                                    </span>
                                </div>
                                <div className="text-[11px] text-foreground/70 line-clamp-1">
                                    {repliedToMessage.media_type && (
                                        <span className="italic">
                                            {repliedToMessage.media_type === 'image' && '📷 Image'}
                                            {repliedToMessage.media_type === 'video' && '🎥 Video'}
                                            {repliedToMessage.media_type === 'audio' && '🎤 Audio'}
                                            {repliedToMessage.media_type === 'other' && '📎 File'}
                                            {repliedToMessage.message ? ': ' : ''}
                                        </span>
                                    )}
                                    {repliedToMessage.message || ''}
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 shrink-0 text-muted-foreground hover:text-foreground"
                                onClick={onCancelReply}
                            >
                                <X className="h-3 w-3" />
                            </Button>
                        </div>
                    )}

                    {/* Textarea */}
                    <Textarea
                        value={message}
                        ref={inputRef}
                        onPaste={handlePaste}
                        onKeyDown={(e) => {
                            if (showMentionSuggestions && mentionSuggestions.length > 0) {
                                if (e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectedMentionIndex(prev =>
                                        prev < mentionSuggestions.length - 1 ? prev + 1 : prev
                                    );
                                } else if (e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectedMentionIndex(prev => prev > 0 ? prev - 1 : prev);
                                } else if (e.key === 'Enter' || e.key === 'Tab') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const selectedUser = mentionSuggestions[selectedMentionIndex];
                                    if (selectedUser) handleMentionSelect(selectedUser);
                                } else if (e.key === 'Escape') {
                                    e.stopPropagation();
                                    setShowMentionSuggestions(false);
                                    setMentionQuery('');
                                }
                            } else if (e.key === 'Enter' && !e.shiftKey) {
                                // Send on Enter (unless mention suggestions are showing or Shift is held)
                                e.preventDefault();
                                if (canSubmit) {
                                    handleSubmit();
                                }
                            }
                        }}
                        onChange={(e) => {
                            const newValue = e.target.value;
                            setMessage(newValue);

                            // Check for @ mention
                            const cursorPos = e.target.selectionStart || newValue.length;
                            const textBeforeCursor = newValue.substring(0, cursorPos);
                            const lastAtIndex = textBeforeCursor.lastIndexOf('@');

                            if (lastAtIndex !== -1) {
                                const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
                                if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
                                    setMentionStartIndex(lastAtIndex);
                                    setMentionQuery(textAfterAt);
                                } else {
                                    setShowMentionSuggestions(false);
                                    setMentionQuery('');
                                }
                            } else {
                                setShowMentionSuggestions(false);
                                setMentionQuery('');
                            }
                        }}
                        placeholder="Type a message..."
                        rows={1}
                        className={cn(
                            "min-h-[36px] max-h-[200px] resize-none py-2 px-1",
                            "bg-transparent border-0 rounded-none",
                            "focus-visible:ring-0 focus-visible:ring-offset-0",
                            "placeholder:text-muted-foreground/60 text-sm"
                        )}
                    />

                    {/* Bottom toolbar */}
                    <div className="flex items-center justify-between pt-1">
                        {/* Left: Attach/Menu button */}
                        {mobileScreen ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted">
                                        <Paperclip className="h-5 w-5" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="start"
                                    side="top"
                                    className="min-w-[160px] z-[1100]"
                                >
                                    {allMediaOptions.map((option) => {
                                        const IconComponent = option.icon;
                                        return (
                                            <DropdownMenuItem
                                                key={option.type}
                                                onClick={() => handleRecordingTypeSelect(option.type)}
                                                className="gap-2"
                                            >
                                                <IconComponent className="h-4 w-4" />
                                                {option.label}
                                            </DropdownMenuItem>
                                        );
                                    })}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className={cn(
                                            "h-8 w-8 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted",
                                            isMediaSelected && "text-primary"
                                        )}
                                    >
                                        <Paperclip className="h-5 w-5" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="start"
                                    side="top"
                                    className="min-w-[160px] z-[1100]"
                                >
                                    <DropdownMenuItem onClick={handleUploadButtonClick} className="gap-2">
                                        <FileUp className="h-4 w-4" />
                                        Upload File
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}

                        {/* Right: Recording buttons + Send */}
                        <div className="flex items-center gap-0.5">
                            {/* Desktop: Recording buttons */}
                            {!mobileScreen && (
                                <div className="flex items-center text-muted-foreground [&_button]:h-8 [&_button]:w-8 [&_button]:rounded-full [&_button:hover]:text-foreground">
                                    <ScreenshotCapture onScreenshotComplete={handleScreenshotComplete} />
                                    <ScreenRecorder onRecordingComplete={handleScreenRecordingComplete} clearRecording={clearScreenRecordingRef} />
                                    <VideoRecorder onRecordingComplete={handleVideoRecordingComplete} clearRecording={clearVideoRecordingRef} />
                                    <AudioRecorder onRecordingComplete={handleRecordingComplete} clearRecording={clearRecordingRef} />
                                </div>
                            )}

                            {/* Mobile: Audio recorder */}
                            {mobileScreen && (
                                <div ref={audioRecorderButtonRef} className="text-muted-foreground [&_button]:h-8 [&_button]:w-8 [&_button]:rounded-full [&_button:hover]:text-foreground">
                                    <AudioRecorder onRecordingComplete={handleRecordingComplete} clearRecording={clearRecordingRef} />
                                </div>
                            )}

                            {/* Send button */}
                            <Button
                                size="icon"
                                className={cn(
                                    "h-8 w-8 rounded-full transition-colors",
                                    canSubmit
                                        ? "bg-primary text-primary-foreground hover:bg-primary/90"
                                        : "bg-muted text-muted-foreground"
                                )}
                                onClick={handleSubmit}
                                disabled={!canSubmit || submitting}
                            >
                                {submitting ? <Clock className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Media Preview Dialog */}
            <Dialog open={showRecordingDialog} onOpenChange={(open) => {
                if (!open) {
                    // When dialog is closed via X button or clicking outside, cancel the recording
                    handleCancelRecording();
                } else {
                    setShowRecordingDialog(open);
                }
            }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center justify-between">
                            <span>
                                {mediaType === 'video' ? 'Video Ready' :
                                    mediaType === 'image' ? 'Image Ready' :
                                        mediaType === 'audio' ? 'Audio Ready' : 'Media Ready'}
                            </span>
                            {mediaFile && (
                                <Badge variant="secondary">{formatFileSize(mediaFile.size)}</Badge>
                            )}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {/* Audio Preview */}
                        {mediaFile && mediaType === 'audio' && (
                            <ReactAudioPlayer
                                src={URL.createObjectURL(mediaFile)}
                                controls
                                className="w-full"
                            />
                        )}

                        {/* Video Preview */}
                        {mediaFile && mediaType === 'video' && (
                            <video
                                controls
                                preload="auto"
                                className="w-full max-h-[400px] rounded-lg"
                            >
                                <source src={URL.createObjectURL(mediaFile)} type={mediaFile.type} />
                                Your browser does not support the video tag.
                            </video>
                        )}

                        {/* Image Preview */}
                        {mediaFile && mediaType === 'image' && (
                            <>
                                {(() => {
                                    try {
                                        const imageUrl = URL.createObjectURL(mediaFile);
                                        return (
                                            <img
                                                src={imageUrl}
                                                alt="Preview"
                                                className={cn(
                                                    "w-full max-h-[400px] object-contain rounded-lg",
                                                    mobileScreen && "border-4 border-muted"
                                                )}
                                                onError={(e) => {
                                                    console.error('Failed to load image preview:', mediaFile.name, mediaFile.type);
                                                    e.target.style.display = 'none';
                                                }}
                                                onLoad={(e) => {
                                                    // Ensure image is visible when loaded
                                                    e.target.style.display = 'block';
                                                }}
                                            />
                                        );
                                    } catch (error) {
                                        console.error('Failed to create object URL for image:', error);
                                        return (
                                            <div className="p-4 text-center text-muted-foreground">
                                                <p>Unable to preview image</p>
                                                <p className="text-xs mt-1">{mediaFile.name}</p>
                                            </div>
                                        );
                                    }
                                })()}

                                {/* Screenshot Quality Selector */}
                                {screenshotCanvas && (
                                    <div className="p-3 bg-muted rounded-lg space-y-3">
                                        <label className="text-sm font-semibold">JPEG Quality:</label>
                                        <TooltipProvider delayDuration={200}>
                                            <div className="flex gap-2">
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            variant={screenshotQuality === '0.75' ? 'default' : 'outline'}
                                                            onClick={() => handleScreenshotQualityChange('0.75')}
                                                            className="px-4 py-2 h-10 font-medium"
                                                        >
                                                            75% (Smaller)
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="bottom">
                                                        <p>Good balance between file size and quality</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            variant={screenshotQuality === '1' ? 'default' : 'outline'}
                                                            onClick={() => handleScreenshotQualityChange('1')}
                                                            className="px-4 py-2 h-10 font-medium"
                                                        >
                                                            100% (Best)
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="bottom">
                                                        <p>Maximum detail, larger file size</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </div>
                                        </TooltipProvider>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Other File Types Preview (PDFs, docs, archives, etc.) */}
                        {mediaFile && mediaType === 'other' && (
                            <div className="flex items-center gap-3 p-4 rounded-lg border border-border bg-muted/50">
                                <div className="w-12 h-12 rounded flex items-center justify-center bg-gray-500 shrink-0">
                                    <FileUp className="h-6 w-6 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-foreground truncate">
                                        {mediaFile.name}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {formatFileSize(mediaFile.size)} • Ready to send
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Caption Input */}
                        <Textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Add a caption (optional)"
                            rows={2}
                        />
                    </div>

                    <DialogFooter className="flex-row justify-between sm:justify-between">
                        <TooltipProvider delayDuration={200}>
                            <div className="flex gap-2">
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="h-12 w-12 rounded-full border-2 border-destructive text-destructive"
                                            onClick={handleCancelRecording}
                                        >
                                            <X className="h-5 w-5" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                        <p>Cancel</p>
                                    </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="h-12 w-12 rounded-full border-2 border-primary text-primary"
                                            onClick={handleDownloadRecording}
                                        >
                                            <Download className="h-5 w-5" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                        <p>Download</p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        size="icon"
                                        className="h-12 w-12 rounded-full"
                                        onClick={handleSendRecording}
                                    >
                                        <Send className="h-5 w-5" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                    <p>Send</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Photo Annotation Dialog */}
            <Dialog open={showAnnotationDialog} onOpenChange={setShowAnnotationDialog}>
                <DialogContent className={cn("max-w-3xl", mobileScreen && "h-screen max-h-screen p-0")}>
                    <DialogHeader className={cn("px-6 pt-6 pb-2", mobileScreen && "px-4 pt-4 pb-2")}>
                        <DialogTitle className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10">
                                    <Brush className="h-5 w-5 text-primary" />
                                </div>
                                <div>
                                    <span className="text-lg font-semibold">Annotate Image</span>
                                    <p className="text-xs text-muted-foreground font-normal mt-0.5">
                                        Draw on the image to highlight areas
                                    </p>
                                </div>
                            </div>
                            <TooltipProvider delayDuration={200}>
                                <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/50">
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant={brushSize === '10' ? 'default' : 'ghost'}
                                                size="icon"
                                                className={cn(
                                                    "h-9 w-9 rounded-md",
                                                    brushSize === '10' && "bg-[#39FF14]/20 text-[#39FF14] hover:bg-[#39FF14]/30"
                                                )}
                                                onClick={() => setBrushSize('10')}
                                            >
                                                <Circle className="h-3 w-3" fill="#39FF14" stroke="#39FF14" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom">
                                            <p>Small brush</p>
                                        </TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant={brushSize === '30' ? 'default' : 'ghost'}
                                                size="icon"
                                                className={cn(
                                                    "h-9 w-9 rounded-md",
                                                    brushSize === '30' && "bg-[#39FF14]/20 text-[#39FF14] hover:bg-[#39FF14]/30"
                                                )}
                                                onClick={() => setBrushSize('30')}
                                            >
                                                <Circle className="h-5 w-5" fill="#39FF14" stroke="#39FF14" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom">
                                            <p>Large brush</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </div>
                            </TooltipProvider>
                        </DialogTitle>
                    </DialogHeader>

                    <div className={cn(
                        "px-6 py-2",
                        mobileScreen && "flex-1 h-[calc(100vh-150px)] px-0 py-0"
                    )}>
                        <canvas
                            ref={(el) => {
                                annotationCanvasRef.current = el;
                                if (el && annotationImage && !imageLoadedRef.current) {
                                    const img = new Image();
                                    img.onload = () => {
                                        el.width = img.width;
                                        el.height = img.height;
                                        const ctx = el.getContext('2d');
                                        ctx.drawImage(img, 0, 0);
                                        ctx.strokeStyle = '#39FF14';
                                        ctx.lineWidth = parseInt(brushSize);
                                        ctx.lineCap = 'round';
                                        ctx.lineJoin = 'round';
                                        imageLoadedRef.current = true;
                                    };
                                    img.src = annotationImage;
                                }
                            }}
                            onMouseDown={(e) => {
                                setIsDrawing(true);
                                const canvas = annotationCanvasRef.current;
                                const rect = canvas.getBoundingClientRect();
                                const ctx = canvas.getContext('2d');
                                ctx.strokeStyle = '#39FF14';
                                ctx.lineWidth = parseInt(brushSize);
                                ctx.lineCap = 'round';
                                ctx.lineJoin = 'round';
                                ctx.beginPath();
                                ctx.moveTo((e.clientX - rect.left) * (canvas.width / rect.width), (e.clientY - rect.top) * (canvas.height / rect.height));
                            }}
                            onMouseMove={(e) => {
                                if (!isDrawing) return;
                                const canvas = annotationCanvasRef.current;
                                const rect = canvas.getBoundingClientRect();
                                const ctx = canvas.getContext('2d');
                                ctx.lineTo((e.clientX - rect.left) * (canvas.width / rect.width), (e.clientY - rect.top) * (canvas.height / rect.height));
                                ctx.stroke();
                            }}
                            onMouseUp={() => setIsDrawing(false)}
                            onMouseLeave={() => setIsDrawing(false)}
                            onTouchStart={(e) => {
                                setIsDrawing(true);
                                const canvas = annotationCanvasRef.current;
                                const rect = canvas.getBoundingClientRect();
                                const ctx = canvas.getContext('2d');
                                const touch = e.touches[0];
                                ctx.strokeStyle = '#39FF14';
                                ctx.lineWidth = parseInt(brushSize);
                                ctx.lineCap = 'round';
                                ctx.lineJoin = 'round';
                                ctx.beginPath();
                                ctx.moveTo((touch.clientX - rect.left) * (canvas.width / rect.width), (touch.clientY - rect.top) * (canvas.height / rect.height));
                            }}
                            onTouchMove={(e) => {
                                if (!isDrawing) return;
                                e.preventDefault();
                                const canvas = annotationCanvasRef.current;
                                const rect = canvas.getBoundingClientRect();
                                const ctx = canvas.getContext('2d');
                                const touch = e.touches[0];
                                ctx.lineTo((touch.clientX - rect.left) * (canvas.width / rect.width), (touch.clientY - rect.top) * (canvas.height / rect.height));
                                ctx.stroke();
                            }}
                            onTouchEnd={() => setIsDrawing(false)}
                            className={cn(
                                "w-full object-contain touch-none rounded-lg cursor-crosshair",
                                mobileScreen ? "h-full" : "max-h-[60vh] border border-border shadow-sm"
                            )}
                        />
                    </div>

                    <DialogFooter className={cn(
                        "flex-row justify-between sm:justify-between gap-3 px-6 pb-6 pt-4",
                        mobileScreen && "px-4 pb-4"
                    )}>
                        <TooltipProvider delayDuration={200}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="lg"
                                        className="gap-2"
                                        onClick={() => {
                                            // Skip annotation but still proceed to preview
                                            setShowAnnotationDialog(false);
                                            setAnnotationImage(null);
                                            imageLoadedRef.current = false;
                                            setBrushSize('10');
                                            // If we have a mediaFile, show the recording dialog
                                            if (mediaFile) {
                                                setIsMediaSelected(true);
                                                setMediaType('image');
                                                // For screenshots, regenerate with default quality
                                                if (screenshotCanvas) {
                                                    setScreenshotQuality('0.75');
                                                    generateScreenshotBlob(screenshotCanvas, 0.75);
                                                } else {
                                                    setShowRecordingDialog(true);
                                                }
                                            }
                                        }}
                                    >
                                        Skip Annotation
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                    <p>Continue without drawing</p>
                                </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        size="lg"
                                        className="gap-2"
                                        onClick={() => {
                                            const canvas = annotationCanvasRef.current;
                                            canvas.toBlob((blob) => {
                                                const humanTimestamp = getHumanReadableTimestamp();
                                                const annotatedFile = new File([blob], `annotated_photo_${humanTimestamp}.jpg`, {
                                                    type: 'image/jpeg',
                                                    lastModified: Date.now()
                                                });
                                                setMediaFile(annotatedFile);
                                                setIsMediaSelected(true);
                                                setMediaType('image');
                                                setShowAnnotationDialog(false);
                                                setAnnotationImage(null);
                                                imageLoadedRef.current = false;
                                                setBrushSize('10');
                                                // Clear screenshotCanvas since we're using the annotated version
                                                setScreenshotCanvas(null);
                                                setShowRecordingDialog(true);
                                            }, 'image/jpeg', 0.8);
                                        }}
                                    >
                                        <Brush className="h-4 w-4" />
                                        Done
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                    <p>Save annotations and continue</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </TooltipProvider>
    );
};

export default CreateMessage;
